import { useCallback } from 'react';
import { cn } from '../../../lib/utils';
import { RepeatPreferenceTypeSelect } from '../repeat-preference-type-select';
import { RepeatByTypeSelect } from '../repeat-by-type-select';
import { WeekdaysSelect } from '../weekdays-select';
import { MonthdaysSelect } from '../monthdays-select';
import type {
  IRepeatPreference,
  customRepeatPreference,
  repeatPreferenceType,
  repeatByType,
  repeatByWeekDetail,
  repeatByMonthDetail,
} from '@baishuyun/types';

export interface RepeatPreferenceSelectProps {
  value?: IRepeatPreference;
  onChange?: (value: IRepeatPreference) => void;
  disabled?: boolean;
  className?: string;
}

const NUMBER_INPUT_CLASSES = cn(
  'h-[34px] px-[10px] py-[7px] rounded-[4px] bg-white border border-[#e0e0e0]',
  'text-[14px] text-[#030303] placeholder:text-[#999999]',
  'focus:outline-none focus:border-[#0265FF]',
  'disabled:bg-[#EFF0F6] disabled:cursor-not-allowed',
  '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
  'w-[60px]',
);

function defaultCustomRepeatPreference(): customRepeatPreference {
  return { repeatBy: 'week', interval: 1, repeatDetailDates: [] };
}

export function RepeatPreferenceSelect({
  value,
  onChange,
  disabled = false,
  className,
}: RepeatPreferenceSelectProps) {
  const selectedType = value?.type;
  const customPref = value?.customRepeatPreference;
  const repeatBy = customPref?.repeatBy;

  const handleTypeChange = useCallback(
    (type: repeatPreferenceType) => {
      if (type === 'custom') {
        onChange?.({
          type: 'custom',
          customRepeatPreference: customPref ?? defaultCustomRepeatPreference(),
        });
      } else {
        onChange?.({ type });
      }
    },
    [onChange, customPref],
  );

  const handleRepeatByChange = useCallback(
    (newRepeatBy: repeatByType) => {
      if (!customPref) return;
      onChange?.({
        type: 'custom',
        customRepeatPreference: {
          ...customPref,
          repeatBy: newRepeatBy,
          repeatDetailDates: [],
        },
      });
    },
    [onChange, customPref],
  );

  const handleIntervalChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!customPref) return;
      const raw = parseInt(e.target.value, 10);
      const interval = isNaN(raw) ? 1 : Math.max(1, raw);
      onChange?.({
        type: 'custom',
        customRepeatPreference: { ...customPref, interval },
      });
    },
    [onChange, customPref],
  );

  const handleWeekdaysChange = useCallback(
    (days: repeatByWeekDetail[]) => {
      if (!customPref) return;
      onChange?.({
        type: 'custom',
        customRepeatPreference: { ...customPref, repeatDetailDates: days },
      });
    },
    [onChange, customPref],
  );

  const handleMonthdaysChange = useCallback(
    (days: repeatByMonthDetail[]) => {
      if (!customPref) return;
      onChange?.({
        type: 'custom',
        customRepeatPreference: { ...customPref, repeatDetailDates: days },
      });
    },
    [onChange, customPref],
  );

  const weekdaysValue = (customPref?.repeatDetailDates ?? []) as repeatByWeekDetail[];
  const monthdaysValue = (customPref?.repeatDetailDates ?? []) as repeatByMonthDetail[];

  return (
    <div className={cn('flex flex-col gap-[10px]', className)}>
      <RepeatPreferenceTypeSelect
        value={selectedType}
        onChange={handleTypeChange}
        disabled={disabled}
      />

      {selectedType === 'custom' && (
        <div className="flex items-start gap-[10px] w-full">
          <RepeatByTypeSelect
            className="w-[200px] shrink-0"
            value={repeatBy}
            onChange={handleRepeatByChange}
            disabled={disabled}
          />
          <span className="text-[14px] text-[#030303] leading-[34px] shrink-0">每</span>
          <input
            type="number"
            value={customPref?.interval ?? 1}
            onChange={handleIntervalChange}
            disabled={disabled}
            min={1}
            step={1}
            className={NUMBER_INPUT_CLASSES}
          />
          <span className="text-[14px] text-[#030303] leading-[34px] shrink-0">
            {repeatBy === 'month' ? '月' : '周'}
          </span>
          {repeatBy === 'week' && (
            <WeekdaysSelect
              className="flex-1 min-w-0"
              value={weekdaysValue}
              onChange={handleWeekdaysChange}
              disabled={disabled}
            />
          )}
          {repeatBy === 'month' && (
            <MonthdaysSelect
              className="flex-1 min-w-0"
              value={monthdaysValue}
              onChange={handleMonthdaysChange}
              disabled={disabled}
            />
          )}
        </div>
      )}
    </div>
  );
}
